import { useEffect, useState } from "react";

export type NavView = "chat" | "ideas" | "todos";

// URL routing: the nav state (selected session / view / open idea) round-trips through the hash so the
// browser back/forward buttons work and views are deep-linkable. navToHash and hashToNav are exact inverses.
function navToHash(selected: string | null, view: NavView, openIdea: string | null): string {
  if (!selected) return "#/";
  let h = `#/s/${selected}`;
  if (view === "ideas") h += openIdea ? `/ideas/${openIdea}` : "/ideas";
  if (view === "todos") h += "/todos";
  return h;
}
function hashToNav(hash: string): { selected: string | null; view: NavView; openIdea: string | null } {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] !== "s" || !parts[1] || !parts[2]) return { selected: null, view: "chat", openIdea: null };
  const selected = `${parts[1]}/${parts[2]}`;
  if (parts[3] === "ideas") return { selected, view: "ideas", openIdea: parts[4] ?? null };
  if (parts[3] === "todos") return { selected, view: "todos", openIdea: null };
  return { selected, view: "chat", openIdea: null };
}

/**
 * The nav state (selected session / view / open idea) synced to the URL hash: applies the hash on load and
 * on browser back/forward (popstate), and pushes a history entry when the state changes (the exact-inverse
 * guard avoids a loop when the change itself came from a popstate).
 */
export function useHashRouter() {
  const [selected, setSelected] = useState<string | null>(null); // "pid/sid"
  const [view, setView] = useState<NavView>("chat");
  const [openIdea, setOpenIdea] = useState<string | null>(null); // channelId

  // Apply the hash on load (deep link) + on browser back/forward.
  useEffect(() => {
    const apply = () => {
      const n = hashToNav(location.hash);
      setSelected(n.selected);
      setView(n.view);
      setOpenIdea(n.openIdea);
    };
    apply();
    window.addEventListener("popstate", apply);
    return () => window.removeEventListener("popstate", apply);
  }, []);

  // Reflect nav state → the hash (one history entry per navigation).
  useEffect(() => {
    const target = navToHash(selected, view, openIdea);
    if ((location.hash || "#/") !== target) history.pushState(null, "", target);
  }, [selected, view, openIdea]);

  return { selected, setSelected, view, setView, openIdea, setOpenIdea };
}
